import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ExternalLink, RefreshCw, AlertCircle, CheckCircle2, FileText, Download, Mail, Power, PowerOff, Eye, Trash2, FilePlus2, Pencil, Send, FileEdit, Wallet, Undo2, UserPlus, BookUser, X } from "lucide-react";
import InvoicePreviewModal from "../../components/InvoicePreviewModal";
import ManualInvoiceModal from "../../components/ManualInvoiceModal";
import PdfViewerModal, { buildInvoicePdfUrls } from "../../components/PdfViewerModal";
import CustomerBookSection from "../../components/CustomerBookSection";
import IssuerBadge from "../../components/IssuerBadge";
import { Link } from "react-router-dom";

export default function AdminFacturalusa() {
  const [items, setItems] = useState([]);
  const [manualItems, setManualItems] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [setting, setSetting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [previewBid, setPreviewBid] = useState(null);
  const [showManualModal, setShowManualModal] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  // PDF viewer state — holds the URLs+title currently being shown in the
  // in-app modal. Open by calling openPdfViewer({...}) on any «Ver» click.
  const [pdfView, setPdfView] = useState(null);
  const [showCustomerBook, setShowCustomerBook] = useState(false);

  // Close the Customer Book modal on Escape for keyboard accessibility.
  useEffect(() => {
    if (!showCustomerBook) return;
    const onKey = (e) => { if (e.key === "Escape") setShowCustomerBook(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showCustomerBook]);

  async function load() {
    setLoading(true);
    try {
      const [{ data: list }, { data: h }, { data: s }, { data: mlist }, { data: dlist }] = await Promise.all([
        api.get("/admin/facturalusa/invoices"),
        api.get("/admin/facturalusa/health"),
        api.get("/admin/settings"),
        api.get("/admin/facturalusa/manual"),
        api.get("/admin/facturalusa/manual/drafts"),
      ]);
      setItems(list.items || []);
      setHealth(h);
      setSettings(s);
      setManualItems(mlist.items || []);
      setDrafts(dlist.items || []);
    } catch (e) {
      toast.error("Não foi possível carregar as faturas Facturalusa.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function toggleFacturalusa() {
    if (!settings) return;
    const next = !settings.facturalusa_paused;  // next paused state
    const confirmMsg = next
      ? "Desativar Facturalusa?\n\nA emissão automática de Fatura-Recibo deixa de acontecer quando marcar uma reserva como paga. Pode reativar a qualquer momento."
      : "Ativar Facturalusa?\n\nA partir de agora, cada reserva marcada como paga emite automaticamente uma Fatura-Recibo.";
    if (!window.confirm(confirmMsg)) return;
    setToggling(true);
    try {
      const { data } = await api.patch("/admin/settings/facturalusa-paused", { paused: next });
      setSettings(data);
      toast.success(next ? "Facturalusa DESATIVADO." : "Facturalusa ATIVADO.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível alterar a definição.");
    } finally {
      setToggling(false);
    }
  }

  async function toggleRevenueVat() {
    if (!settings) return;
    const next = !settings.revenue_vat_enabled;
    const rate = settings.revenue_vat_rate || 22;
    const confirmMsg = next
      ? `Ativar IVA nas faturas de receita?\n\nA partir de agora, as próximas faturas emitidas terão IVA de ${rate}% adicionado ao valor base (regime normal).\n\nNão afeta as faturas já emitidas.`
      : "Desativar IVA nas faturas de receita?\n\nVoltamos ao regime de isenção do art. 53.º do CIVA. As próximas faturas serão emitidas sem IVA (M10).";
    if (!window.confirm(confirmMsg)) return;
    setToggling(true);
    try {
      const { data } = await api.patch("/admin/settings/revenue-vat", { enabled: next });
      setSettings(data);
      toast.success(next ? `IVA ATIVADO (${rate}%).` : "IVA DESATIVADO (regime isento).");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível alterar a definição.");
    } finally {
      setToggling(false);
    }
  }

  async function setRevenueVatRate() {
    if (!settings) return;
    const current = settings.revenue_vat_rate || 22;
    const input = window.prompt(
      `Taxa de IVA a aplicar nas faturas de receita (0–100).\n\nTaxas comuns na Madeira:\n• 22% — taxa normal\n• 12% — taxa intermédia\n• 5%  — taxa reduzida`,
      String(current),
    );
    if (input == null) return;
    const rate = parseFloat(input.replace(",", "."));
    if (Number.isNaN(rate) || rate < 0 || rate > 100) {
      toast.error("Taxa inválida — indique um número entre 0 e 100.");
      return;
    }
    try {
      const { data } = await api.patch("/admin/settings/revenue-vat", {
        enabled: settings.revenue_vat_enabled, rate,
      });
      setSettings(data);
      toast.success(`Taxa atualizada para ${rate}%.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível alterar a taxa.");
    }
  }

  async function setPaymentInstructions() {
    if (!settings) return;
    const example = "IBAN: PT50 0000 0000 0000 0000 0000 0\nMB WAY: +351 967 386 076\nRevolut: @luxstaymadeira\nReferência: nº da fatura";
    const input = window.prompt(
      "Instruções de pagamento (uma por linha).\n\n" +
      "Aparece nas OBSERVAÇÕES do PDF da fatura E numa caixa destacada no email que anexa o PDF.\n\n" +
      "Exemplo:\n" + example,
      settings.payment_instructions || example,
    );
    if (input == null) return;
    try {
      const { data } = await api.patch("/admin/settings/payment-instructions", { text: input });
      setSettings(data);
      toast.success(input.trim() ? "Instruções de pagamento guardadas." : "Instruções de pagamento removidas.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível guardar as instruções.");
    }
  }

  async function runSetup() {
    setSetting(true);
    try {
      const { data } = await api.post("/admin/facturalusa/setup");
      if (data.communicated_at_ok) {
        toast.success("Série criada e comunicada à AT com sucesso.");
      } else {
        toast.warning(data.communicate_error || "Série criada mas falta configurar AT.");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no setup.");
    } finally {
      setSetting(false);
    }
  }

  async function retry(bid) {
    try {
      await api.post(`/admin/facturalusa/issue/${bid}?force=true`);
      toast.success("Fatura reemitida.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falhou a reemissão.");
    }
  }

  async function issueNow(bid) {
    try {
      await api.post(`/admin/facturalusa/issue/${bid}`);
      toast.success("Fatura emitida e enviada ao cliente.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falhou a emissão.");
    }
  }

  async function sendEmail(bid) {
    try {
      const { data } = await api.post(`/admin/facturalusa/invoices/${bid}/send-email`);
      toast.success(
        data?.pdf_attached
          ? "Email reenviado ao cliente (com PDF anexado)."
          : "Email reenviado ao cliente."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível reenviar o email.");
    }
  }

  function downloadPdf(bid, number) {
    // Backend force-streams the PDF with a friendly filename and
    // Content-Disposition: attachment, so a plain <a target=_blank> works
    // on desktop & mobile (including iOS Safari and the in-app webview).
    const a = document.createElement("a");
    a.href = `${api.defaults.baseURL}/admin/facturalusa/invoices/${bid}/pdf`;
    a.target = "_blank";
    a.rel = "noreferrer";
    a.download = `${number || "factura"}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function viewPdf(bid, number) {
    // Open the PDF inside the app (modal with iframe) — keeps the admin
    // inside the panel with a clear «Fechar» and «Descarregar» button.
    setPdfView({
      title: `Fatura ${number || ""}`.trim(),
      ...buildInvoicePdfUrls({ bookingId: bid, number }),
    });
  }

  function downloadManualPdf(invoiceId, number) {
    const a = document.createElement("a");
    a.href = `${api.defaults.baseURL}/admin/facturalusa/manual/${invoiceId}/pdf`;
    a.target = "_blank";
    a.rel = "noreferrer";
    a.download = `${number || "factura"}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function viewManualPdf(invoiceId, number) {
    setPdfView({
      title: `Fatura ${number || ""}`.trim(),
      ...buildInvoicePdfUrls({ manualInvoiceId: invoiceId, number }),
    });
  }

  async function resendManualEmail(invoiceId) {
    try {
      const { data } = await api.post(`/admin/facturalusa/manual/${invoiceId}/send-email`);
      toast.success(
        data?.pdf_attached
          ? "Email reenviado ao cliente (com PDF anexado)."
          : "Email reenviado ao cliente."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível reenviar o email.");
    }
  }

  async function saveManualCustomer(invoiceId, customerName) {
    // Guarda o cliente da fatura no livro para reutilização futura, mesmo
    // depois da fatura estar emitida. Backend valida (precisa de NIF ou email).
    if (!window.confirm(`Guardar «${customerName || 'este cliente'}» no livro de clientes?\n\nNa próxima emissão poderá selecioná-lo automaticamente.`)) return;
    try {
      const { data } = await api.post(`/admin/facturalusa/manual/${invoiceId}/save-customer`);
      toast.success(`Cliente «${data.customer?.name || customerName}» guardado no livro.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível guardar o cliente.");
    }
  }

  async function issueCreditNote(invoiceId, invoiceNumber, invoiceAmount) {
    // Emite uma Nota de Crédito referenciando a fatura original.
    // FL exige que o valor da NC seja ≤ ao total da fatura.
    const amountStr = window.prompt(
      `Emitir NOTA DE CRÉDITO para a fatura ${invoiceNumber}\n\nValor a estornar/descontar em € (máximo ${(invoiceAmount || 0).toFixed(2)}€):`,
      "",
    );
    if (amountStr == null) return;
    const amount = parseFloat(amountStr.replace(",", "."));
    if (Number.isNaN(amount) || amount <= 0) {
      toast.error("Valor inválido — indique um número > 0.");
      return;
    }
    const description = window.prompt(
      "Motivo/descrição da nota de crédito:\n\n(Ex: «Desconto acordado por atraso na chegada da equipa» · «Estorno parcial por serviço incompleto»)",
      "",
    );
    if (description == null) return;
    if (!description.trim()) {
      toast.error("A nota de crédito precisa de uma descrição.");
      return;
    }
    // Dois modos: emitir na Facturalusa OU registar só internamente.
    //  - «OK»   → tentar emitir na Facturalusa (fluxo normal)
    //  - «Cancelar» → sair sem fazer nada
    //  - «Registar apenas internamente» → prompt explícito abaixo
    const emitChoice = window.confirm(
      `EMITIR na Facturalusa a Nota de Crédito de ${amount.toFixed(2)}€ sobre a fatura ${invoiceNumber}?\n\n` +
      `Motivo: ${description}\n\n` +
      `• OK = emite agora na Facturalusa (e regista no app)\n` +
      `• Cancelar = NÃO emite agora. Pergunta a seguir se quer apenas registar internamente ` +
      `(útil se já emitiu a NC manualmente no portal da Facturalusa).`
    );
    let internalOnly = false;
    let referenceNumber = null;
    if (!emitChoice) {
      // Oferecer o fluxo interno.
      const useInternal = window.confirm(
        `Registar apenas INTERNAMENTE a NC de ${amount.toFixed(2)}€?\n\n` +
        `Use isto quando já emitiu a nota de crédito directamente no portal da ` +
        `Facturalusa — o app vai apenas descontar o valor nas finanças, sem chamar a FL.\n\n` +
        `OK = registar só internamente · Cancelar = sair sem fazer nada`
      );
      if (!useInternal) return;
      referenceNumber = window.prompt(
        "Número da NC já emitida na Facturalusa (ex: «NC A/5»):",
        "",
      );
      if (!referenceNumber || !referenceNumber.trim()) {
        toast.error("Indique o número da NC emitida na Facturalusa.");
        return;
      }
      internalOnly = true;
    }
    try {
      const payload = { amount, description: description.trim() };
      if (internalOnly) {
        payload.internal_only = true;
        payload.reference_number = referenceNumber.trim();
      }
      const { data } = await api.post(
        `/admin/facturalusa/invoices/${invoiceId}/credit-note`,
        payload,
      );
      toast.success(
        internalOnly
          ? `Nota de crédito ${data.credit_note_number} registada internamente (−${data.amount.toFixed(2)}€).`
          : `Nota de crédito ${data.credit_note_number || ""} emitida (−${data.amount.toFixed(2)}€).`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível emitir a nota de crédito.");
    }
  }

  async function toggleManualPaid(invoiceId, nextPaid) {
    try {
      await api.patch(`/admin/facturalusa/manual/${invoiceId}/paid`, null, {
        params: { paid: nextPaid },
      });
      toast.success(nextPaid
        ? "Fatura marcada como paga. Já conta nas finanças."
        : "Marca de pagamento removida.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível atualizar.");
    }
  }

  async function deleteDraft(draftId) {
    if (!window.confirm("Eliminar este rascunho? Esta ação é irreversível.")) return;
    try {
      await api.delete(`/admin/facturalusa/manual/drafts/${draftId}`);
      toast.success("Rascunho eliminado.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível eliminar.");
    }
  }

  async function issueDraftDirect(draftId) {
    try {
      const { data } = await api.post(`/admin/facturalusa/manual/drafts/${draftId}/issue`);
      toast.success(`Fatura ${data.invoice_number || ""} emitida com sucesso.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rascunho incompleto ou erro na emissão.");
    }
  }

  function draftSummary(draft) {
    const name = draft.customer?.name || "Cliente sem nome";
    const totalDraft = (draft.lines || []).reduce((acc, l) => {
      const q = parseFloat(l.quantity) || 0;
      const p = parseFloat(l.unit_price) || 0;
      return acc + q * p;
    }, 0);
    const missing = [];
    if (!draft.customer?.name) missing.push("nome");
    if (!(draft.lines || []).length) missing.push("linhas");
    else {
      const incomplete = (draft.lines || []).some((l) => !l.description || !l.unit_price || !l.quantity);
      if (incomplete) missing.push("linhas incompletas");
    }
    return { name, totalDraft, missing };
  }

  async function deleteBooking(bid) {
    if (!window.confirm("Eliminar esta reserva paga (sem fatura emitida)?\n\nA reserva será removida permanentemente. Esta ação é IRREVERSÍVEL.\n\nPara reservas já emitidas, use a Facturalusa para emitir uma nota de crédito.")) return;
    try {
      await api.delete(`/bookings/${bid}`);
      toast.success("Reserva eliminada.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível eliminar.");
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-facturalusa">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">
            Faturação · Facturalusa
          </h1>
          <p className="text-sm text-emerald-200/70 mt-1">
            Faturas-Recibo emitidas automaticamente após confirmação de pagamento.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowCustomerBook(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-amber-300/40 bg-amber-300/10 px-4 py-2 text-sm font-semibold text-amber-100 hover:bg-amber-300/20 transition"
            data-testid="fl-open-customer-book"
            title="Ver histórico e total faturado por cada cliente guardado"
          >
            <BookUser size={14} /> Livro de Clientes
          </button>
          <button
            onClick={() => setShowManualModal(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-amber-300 hover:bg-amber-200 text-emerald-950 px-4 py-2 text-sm font-bold transition shadow-lg shadow-amber-300/20"
            data-testid="fl-manual-issue-btn"
            disabled={settings?.facturalusa_paused}
            title={settings?.facturalusa_paused ? "Facturalusa está desativado" : "Emitir nova fatura"}
          >
            <FilePlus2 size={14} /> Nova Fatura
          </button>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 rounded-xl border border-emerald-300/20 bg-emerald-300/5 px-4 py-2 text-sm text-emerald-100 hover:bg-emerald-300/10 transition"
            data-testid="fl-refresh"
          >
            <RefreshCw size={14} /> Atualizar
          </button>
        </div>
      </div>

      {/* Master ON/OFF toggle for the whole Facturalusa integration */}
      {settings && (
        <div
          className={`rounded-2xl border p-5 flex items-center justify-between gap-4 flex-wrap ${
            settings.facturalusa_paused
              ? "border-red-400/40 bg-red-400/10"
              : "border-emerald-400/40 bg-emerald-400/5"
          }`}
          data-testid="facturalusa-master-toggle"
        >
          <div className="flex items-start gap-3 min-w-0">
            {settings.facturalusa_paused
              ? <PowerOff size={24} className="text-red-300 mt-0.5 shrink-0" />
              : <Power    size={24} className="text-emerald-300 mt-0.5 shrink-0" />}
            <div className="min-w-0">
              <div className="text-white text-base font-semibold flex items-center gap-2 flex-wrap">
                Facturalusa está
                <span className={settings.facturalusa_paused ? "text-red-300" : "text-emerald-300"}>
                  {settings.facturalusa_paused ? "DESATIVADO" : "ATIVADO"}
                </span>
              </div>
              <div className="text-xs text-emerald-200/70 mt-1 max-w-xl">
                {settings.facturalusa_paused
                  ? "A emissão automática de Fatura-Recibo está PAUSADA. Pode marcar reservas como pagas, mas nenhuma fatura sai. Para retomar, clique em «Ativar Facturalusa»."
                  : "Cada reserva marcada como paga emite automaticamente uma Fatura-Recibo na Facturalusa e o PDF é enviado ao cliente."}
              </div>
              {settings.facturalusa_paused && settings.facturalusa_paused_at && (
                <div className="text-[11px] text-red-200/80 mt-2 font-mono">
                  Desativado em {new Date(settings.facturalusa_paused_at).toLocaleString("pt-PT")}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={toggleFacturalusa}
            disabled={toggling}
            data-testid="toggle-facturalusa-master"
            className={`rounded-lg px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition disabled:opacity-50 shrink-0 inline-flex items-center gap-2 ${
              settings.facturalusa_paused
                ? "bg-emerald-400 hover:bg-emerald-300 text-emerald-950"
                : "bg-red-400 hover:bg-red-300 text-red-950"
            }`}>
            {toggling
              ? "..."
              : settings.facturalusa_paused
                ? <><Power size={14} /> Ativar Facturalusa</>
                : <><PowerOff size={14} /> Desativar Facturalusa</>}
          </button>
        </div>
      )}

      {/* Toggle do IVA nas faturas de receita (regime de isenção vs. regime normal) */}
      {settings && (
        <div
          className={`rounded-2xl border p-5 flex items-center justify-between gap-4 flex-wrap ${
            settings.revenue_vat_enabled
              ? "border-amber-300/40 bg-amber-300/5"
              : "border-emerald-300/20 bg-emerald-300/5"
          }`}
          data-testid="revenue-vat-toggle"
        >
          <div className="flex items-start gap-3 min-w-0">
            <Wallet size={24} className={settings.revenue_vat_enabled ? "text-amber-300 mt-0.5 shrink-0" : "text-emerald-300 mt-0.5 shrink-0"} />
            <div className="min-w-0">
              <div className="text-white text-base font-semibold flex items-center gap-2 flex-wrap">
                IVA nas faturas de receita está
                <span className={settings.revenue_vat_enabled ? "text-amber-300" : "text-emerald-300"}>
                  {settings.revenue_vat_enabled ? `ATIVADO (${settings.revenue_vat_rate}%)` : "DESATIVADO"}
                </span>
              </div>
              <div className="text-xs text-emerald-200/70 mt-1 max-w-2xl leading-relaxed">
                {settings.revenue_vat_enabled
                  ? <>As próximas faturas emitidas vão ter <strong className="text-amber-100">IVA de {settings.revenue_vat_rate}% adicionado</strong> ao preço base. O total da fatura passará a refletir base + IVA. Não afeta faturas já emitidas.</>
                  : <>Estamos no <strong className="text-emerald-100">regime de isenção (art. 53.º do CIVA)</strong>. As faturas saem com IVA 0% e nota M10. Ative quando ultrapassar o limite de €15.000/ano ou for opcional.</>}
              </div>
              {settings.revenue_vat_toggled_at && (
                <div className="text-[11px] text-emerald-200/60 mt-2 font-mono">
                  Última alteração: {new Date(settings.revenue_vat_toggled_at).toLocaleString("pt-PT")}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <button
              onClick={setRevenueVatRate}
              disabled={toggling}
              data-testid="set-revenue-vat-rate"
              className="rounded-lg px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition border border-emerald-300/30 bg-emerald-300/5 hover:bg-emerald-300/10 text-emerald-100 inline-flex items-center justify-center gap-2"
              title="Alterar taxa de IVA"
            >
              <Pencil size={14} /> Taxa: {settings.revenue_vat_rate}%
            </button>
            <button
              onClick={toggleRevenueVat}
              disabled={toggling}
              data-testid="toggle-revenue-vat"
              className={`rounded-lg px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition disabled:opacity-50 inline-flex items-center justify-center gap-2 ${
                settings.revenue_vat_enabled
                  ? "bg-emerald-400 hover:bg-emerald-300 text-emerald-950"
                  : "bg-amber-400 hover:bg-amber-300 text-amber-950"
              }`}
            >
              {toggling
                ? "..."
                : settings.revenue_vat_enabled
                  ? <><PowerOff size={14} /> Desativar IVA</>
                  : <><Power size={14} /> Ativar IVA</>}
            </button>
          </div>
        </div>
      )}

      {/* Instruções de pagamento — aparecem nas faturas emitidas + email */}
      {settings && (
        <div
          className={`rounded-2xl border p-5 flex items-start justify-between gap-4 flex-wrap ${
            settings.payment_instructions
              ? "border-[#d4af37]/40 bg-[#d4af37]/5"
              : "border-emerald-300/20 bg-emerald-300/5"
          }`}
          data-testid="payment-instructions-card"
        >
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <Wallet size={24} className={settings.payment_instructions ? "text-[#d4af37] mt-0.5 shrink-0" : "text-emerald-300 mt-0.5 shrink-0"} />
            <div className="min-w-0">
              <div className="text-white text-base font-semibold flex items-center gap-2 flex-wrap">
                Instruções de pagamento
                <span className={settings.payment_instructions ? "text-[#d4af37]" : "text-emerald-300/70"}>
                  {settings.payment_instructions ? "· ATIVAS" : "· AUTOMÁTICAS (Dados da Empresa)"}
                </span>
              </div>
              <div className="text-xs text-emerald-200/70 mt-1 max-w-2xl leading-relaxed">
                {settings.payment_instructions
                  ? <>Aparece nas <strong className="text-[#d4af37]">observações do PDF</strong> e numa caixa destacada no <strong>email com o PDF anexado</strong>. Cada linha aparece como um bullet.</>
                  : <>Sem texto personalizado: as faturas usam automaticamente o IBAN e MB WAY definidos em{" "}
                      <Link to="/admin/empresa" className="text-[#d4af37] underline" data-testid="fl-payment-instructions-company-link">Dados da Empresa</Link>.
                      Pode personalizar (Revolut, referências, etc.) — o texto substitui os dados automáticos.</>}
              </div>
              {settings.payment_instructions && (
                <pre className="mt-3 text-[11px] text-emerald-100 bg-black/25 rounded-md p-3 border border-[#1a4640] font-mono whitespace-pre-wrap max-h-32 overflow-y-auto"
                     data-testid="payment-instructions-preview">
                  {settings.payment_instructions}
                </pre>
              )}
            </div>
          </div>
          <button
            onClick={setPaymentInstructions}
            className="rounded-lg px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition border border-[#d4af37]/40 bg-[#d4af37]/10 hover:bg-[#d4af37]/20 text-[#d4af37] inline-flex items-center justify-center gap-2 shrink-0"
            data-testid="edit-payment-instructions"
          >
            <Pencil size={14} /> {settings.payment_instructions ? "Editar" : "Configurar"}
          </button>
        </div>
      )}

      {/* Health */}
      {health && (
        <div className="rounded-2xl border border-amber-300/20 bg-amber-300/5 p-5" data-testid="fl-health">
          <div className="flex items-start gap-3">
            {health.serie_id ? (
              <CheckCircle2 size={20} className="text-emerald-300 mt-0.5" />
            ) : (
              <AlertCircle size={20} className="text-amber-300 mt-0.5" />
            )}
            <div className="flex-1 text-sm">
              <p className="text-white font-medium">Estado da integração</p>
              <ul className="mt-1.5 text-emerald-200/80 space-y-0.5">
                <li>Chave API: <span className={health.api_key_present ? "text-emerald-300" : "text-red-300"}>{health.api_key_present ? "✓ presente" : "✗ em falta"}</span></li>
                <li>Série ativa: <span className="text-emerald-100 font-mono">{health.serie}</span> {health.serie_id ? <span className="text-emerald-300">(id {health.serie_id})</span> : <span className="text-red-300">— não criada</span>}</li>
              </ul>
              {health.error && (
                <p className="mt-2 text-amber-200">{health.error}</p>
              )}
              {!health.serie_id && (
                <button
                  onClick={runSetup}
                  disabled={setting}
                  className="mt-3 rounded-lg bg-amber-300 hover:bg-amber-200 disabled:opacity-50 text-emerald-950 px-4 py-2 text-xs font-bold"
                  data-testid="fl-setup"
                >
                  {setting ? "A configurar…" : "Configurar série + comunicar AT"}
                </button>
              )}
              <p className="mt-3 text-xs text-emerald-200/60">
                💡 Antes de emitir faturas, configure o <strong>utilizador AT</strong>{" "}
                em <a href="https://facturalusa.pt/login" target="_blank" rel="noreferrer" className="underline text-amber-200">Facturalusa → Definições → AT</a>.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      <div className="rounded-2xl border border-emerald-300/15 bg-emerald-950/40 overflow-hidden">
        <div className="px-4 sm:px-5 py-3 border-b border-emerald-300/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
          <h2 className="text-white font-semibold">Reservas pagas ({items.length})</h2>
          <span className="text-[11px] text-emerald-200/60">
            Inclui reservas pagas com Facturalusa desativado — pode emitir/reenviar a qualquer momento.
          </span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-emerald-200/60 text-sm">A carregar…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center" data-testid="fl-empty">
            <FileText size={36} className="mx-auto text-emerald-300/40 mb-2" />
            <p className="text-emerald-200/70 text-sm">
              Ainda não há reservas pagas. Vão aparecer aqui assim que confirmar um pagamento.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-emerald-300/10">
            {items.map((it) => {
              const issued = it.status === "issued";
              const hasError = it.status === "error" || !!it.error;
              return (
                <div key={it.booking_id}
                     className="p-4 sm:p-5 hover:bg-emerald-300/5 transition"
                     data-testid={`fl-row-${it.booking_id}`}>
                  {/* Mobile-first: information stacks vertically, action
                      buttons full-width below. On md+, info and buttons
                      sit side-by-side. */}
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {issued && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-400/15 text-emerald-200 uppercase tracking-wider font-semibold">EMITIDA</span>
                        )}
                        {!issued && !hasError && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-200 uppercase tracking-wider font-semibold">POR EMITIR</span>
                        )}
                        {hasError && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-400/15 text-red-200 uppercase tracking-wider font-semibold">ERRO</span>
                        )}
                        <span className="font-mono text-amber-300 font-semibold text-sm sm:text-base">
                          {it.invoice_number || `Reserva ${it.booking_id?.slice(0, 8)}`}
                        </span>
                        {issued && <IssuerBadge issuer={it.issuer} testId={`fl-issuer-badge-${it.booking_id}`} />}
                      </div>
                      <p className="text-sm text-emerald-100 truncate">
                        {it.apt_type || "—"} · {it.scheduled_date || "—"} {it.scheduled_time || ""} ·{" "}
                        <span className="font-bold">{(it.total || 0).toFixed(2)}€</span>
                        {it.client_name && <> · {it.client_name}</>}
                      </p>
                      <p className="text-xs text-emerald-200/50 mt-1">
                        {issued
                          ? (it.issued_at && `Emitida em ${new Date(it.issued_at).toLocaleString("pt-PT")}`)
                          : (it.paid_at && `Marcada como paga em ${new Date(it.paid_at).toLocaleString("pt-PT")}`)}
                      </p>
                      {it.error && (
                        <p className="text-xs text-red-300 mt-1 break-words">{it.error}</p>
                      )}
                    </div>
                    {/* Action buttons — wrap to multiple rows on small
                        screens, share width evenly via flex-1 min-w. */}
                    <div className="flex items-center gap-2 flex-wrap md:shrink-0 md:justify-end">
                      {issued && (
                        <button onClick={() => viewPdf(it.booking_id, it.invoice_number)}
                           className="flex-1 md:flex-none min-w-[110px] justify-center rounded-lg border border-emerald-300/20 bg-emerald-300/5 px-3 py-2 text-xs text-emerald-100 hover:bg-emerald-300/10 inline-flex items-center gap-1.5"
                           data-testid={`fl-pdf-${it.booking_id}`}
                           title="Ver no app">
                          <Eye size={12} /> Ver
                        </button>
                      )}
                      {issued && (
                        <>
                          <button onClick={() => setPreviewBid(it.booking_id)}
                                  className="flex-1 md:flex-none min-w-[110px] justify-center rounded-lg border border-emerald-300/20 bg-emerald-300/5 px-3 py-2 text-xs text-emerald-100 hover:bg-emerald-300/10 inline-flex items-center gap-1.5"
                                  data-testid={`fl-details-${it.booking_id}`}>
                            <Eye size={12} /> Detalhes
                          </button>
                          <button onClick={() => downloadPdf(it.booking_id, it.invoice_number)}
                                  className="flex-1 md:flex-none min-w-[110px] justify-center rounded-lg border border-emerald-300/20 bg-emerald-300/5 px-3 py-2 text-xs text-emerald-100 hover:bg-emerald-300/10 inline-flex items-center gap-1.5"
                                  data-testid={`fl-download-${it.booking_id}`}>
                            <Download size={12} /> PDF
                          </button>
                          <button onClick={() => sendEmail(it.booking_id)}
                                  className="flex-1 md:flex-none min-w-[110px] justify-center rounded-lg border border-amber-300/30 bg-amber-300/10 px-3 py-2 text-xs text-amber-100 hover:bg-amber-300/15 inline-flex items-center gap-1.5"
                                  data-testid={`fl-email-${it.booking_id}`}>
                            <Mail size={12} /> Reenviar
                          </button>
                          <button onClick={() => issueCreditNote(it.invoice_id, it.invoice_number, it.total)}
                                  className="flex-1 md:flex-none min-w-[130px] justify-center rounded-lg border border-rose-400/40 bg-rose-400/10 px-3 py-2 text-xs text-rose-100 hover:bg-rose-400/20 inline-flex items-center gap-1.5"
                                  data-testid={`fl-credit-note-${it.booking_id}`}
                                  title="Emitir Nota de Crédito para esta fatura">
                            <Undo2 size={12} /> Nota de Crédito
                          </button>
                        </>
                      )}
                      {!issued && !hasError && (
                        <>
                          <button onClick={() => setPreviewBid(it.booking_id)}
                                  className="flex-1 md:flex-none min-w-[110px] justify-center rounded-lg border border-emerald-300/20 bg-emerald-300/5 px-3 py-2 text-xs text-emerald-100 hover:bg-emerald-300/10 inline-flex items-center gap-1.5"
                                  data-testid={`fl-details-${it.booking_id}`}>
                            <Eye size={12} /> Detalhes
                          </button>
                          <button onClick={() => issueNow(it.booking_id)}
                                  className="flex-1 md:flex-none min-w-[110px] justify-center rounded-lg bg-amber-300 hover:bg-amber-200 text-emerald-950 px-3 py-2 text-xs font-bold inline-flex items-center gap-1.5"
                                  data-testid={`fl-issue-${it.booking_id}`}>
                            <Mail size={12} /> Emitir
                          </button>
                          <button onClick={() => deleteBooking(it.booking_id)}
                                  className="flex-1 md:flex-none min-w-[110px] justify-center rounded-lg border border-red-400/40 bg-red-400/5 px-3 py-2 text-xs text-red-200 hover:bg-red-400/15 inline-flex items-center gap-1.5"
                                  data-testid={`fl-delete-${it.booking_id}`}>
                            <Trash2 size={12} /> Eliminar
                          </button>
                        </>
                      )}
                      {hasError && (
                        <button onClick={() => retry(it.booking_id)}
                                className="flex-1 md:flex-none min-w-[110px] justify-center rounded-lg bg-amber-300 hover:bg-amber-200 text-emerald-950 px-3 py-2 text-xs font-bold"
                                data-testid={`fl-retry-${it.booking_id}`}>
                          Reemitir
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Rascunhos de Faturas — incomplete drafts, can be edited/issued later */}
      {drafts.length > 0 && (
        <div className="rounded-2xl border border-amber-300/30 bg-amber-300/5 overflow-hidden" data-testid="fl-drafts-section">
          <div className="px-4 sm:px-5 py-3 border-b border-amber-300/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
            <h2 className="text-white font-semibold flex items-center gap-2">
              <FileEdit size={16} className="text-amber-300" /> Rascunhos ({drafts.length})
            </h2>
            <span className="text-[11px] text-amber-200/70">
              Faturas guardadas mas ainda não emitidas — clique em «Editar» para continuar.
            </span>
          </div>
          <div className="divide-y divide-amber-300/10">
            {drafts.map((d) => {
              const { name, totalDraft, missing } = draftSummary(d);
              const canIssue = missing.length === 0 && totalDraft > 0;
              return (
                <div key={d.id} className="p-4 sm:p-5 hover:bg-amber-300/5 transition"
                     data-testid={`fl-draft-row-${d.id}`}>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-200 uppercase tracking-wider font-semibold">RASCUNHO</span>
                        <span className="text-white font-semibold text-sm sm:text-base truncate">{name}</span>
                      </div>
                      <p className="text-sm text-emerald-100">
                        Total provisório: <span className="font-bold text-amber-300">{totalDraft.toFixed(2)}€</span>
                        {d.customer?.nif && <> · NIF {d.customer.nif}</>}
                        {d.customer?.email && <> · {d.customer.email}</>}
                      </p>
                      <p className="text-xs text-emerald-200/50 mt-1">
                        Guardado em {new Date(d.updated_at || d.created_at).toLocaleString("pt-PT")}
                        {missing.length > 0 && (
                          <span className="text-amber-300 ml-2">· Em falta: {missing.join(", ")}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap md:shrink-0 md:justify-end">
                      <button onClick={() => setEditingDraft(d)}
                              className="flex-1 md:flex-none min-w-[100px] justify-center rounded-lg border border-emerald-300/30 bg-emerald-300/10 px-3 py-2 text-xs text-emerald-100 hover:bg-emerald-300/20 inline-flex items-center gap-1.5"
                              data-testid={`fl-draft-edit-${d.id}`}>
                        <Pencil size={12} /> Editar
                      </button>
                      <button onClick={() => issueDraftDirect(d.id)}
                              disabled={!canIssue || settings?.facturalusa_paused}
                              className="flex-1 md:flex-none min-w-[100px] justify-center rounded-lg bg-amber-300 hover:bg-amber-200 disabled:opacity-40 disabled:cursor-not-allowed text-emerald-950 px-3 py-2 text-xs font-bold inline-flex items-center gap-1.5"
                              title={!canIssue ? "Preencha os campos em falta antes de emitir" : "Emitir agora"}
                              data-testid={`fl-draft-issue-${d.id}`}>
                        <Send size={12} /> Emitir
                      </button>
                      <button onClick={() => deleteDraft(d.id)}
                              className="flex-1 md:flex-none min-w-[100px] justify-center rounded-lg border border-red-400/40 bg-red-400/5 px-3 py-2 text-xs text-red-200 hover:bg-red-400/15 inline-flex items-center gap-1.5"
                              data-testid={`fl-draft-delete-${d.id}`}>
                        <Trash2 size={12} /> Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Faturas emitidas sem reserva associada */}
      <div className="rounded-2xl border border-amber-300/20 bg-emerald-950/40 overflow-hidden">
        <div className="px-4 sm:px-5 py-3 border-b border-amber-300/15 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
          <h2 className="text-white font-semibold flex items-center gap-2">
            <FilePlus2 size={16} className="text-amber-300" /> Outras Faturas ({manualItems.length})
          </h2>
          <span className="text-[11px] text-emerald-200/60">
            Faturas emitidas diretamente, sem reserva associada.
          </span>
        </div>
        {manualItems.length === 0 ? (
          <div className="p-8 text-center" data-testid="fl-manual-empty">
            <FilePlus2 size={32} className="mx-auto text-amber-300/40 mb-2" />
            <p className="text-emerald-200/70 text-sm">
              Ainda não há faturas emitidas diretamente. Clique em «Nova Fatura» no topo.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-emerald-300/10">
            {manualItems.map((it) => {
              const isPaid = it.paid === true;
              return (
              <div key={it.invoice_id}
                   className="p-4 sm:p-5 hover:bg-emerald-300/5 transition"
                   data-testid={`fl-manual-row-${it.invoice_id}`}>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-400/15 text-emerald-200 uppercase tracking-wider font-semibold">EMITIDA</span>
                      {isPaid ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-400/25 text-emerald-100 uppercase tracking-wider font-bold inline-flex items-center gap-1"
                              data-testid={`fl-manual-paid-badge-${it.invoice_id}`}>
                          <CheckCircle2 size={10} /> PAGA
                        </span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-200 uppercase tracking-wider font-bold inline-flex items-center gap-1"
                              data-testid={`fl-manual-unpaid-badge-${it.invoice_id}`}>
                          <AlertCircle size={10} /> POR RECEBER
                        </span>
                      )}
                      <span className="font-mono text-amber-300 font-semibold text-sm sm:text-base">
                        {it.invoice_number || `#${it.invoice_id}`}
                      </span>
                      <IssuerBadge issuer={it.issuer} testId={`fl-manual-issuer-badge-${it.invoice_id}`} />
                    </div>
                    <p className="text-sm text-emerald-100 truncate">
                      <span className="font-bold">{(it.amount || 0).toFixed(2)}€</span>
                      {" · "}{it.customer_name || "—"}
                      {it.customer_nif && it.customer_nif !== "999999990" && (
                        <> · NIF {it.customer_nif}</>
                      )}
                    </p>
                    <p className="text-xs text-emerald-200/50 mt-1">
                      {it.issued_at && `Emitida em ${new Date(it.issued_at).toLocaleString("pt-PT")}`}
                      {isPaid && it.paid_at && (
                        <span className="text-emerald-300"> · Paga em {new Date(it.paid_at).toLocaleString("pt-PT")}</span>
                      )}
                      {it.customer_email && <> · {it.customer_email}</>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap md:shrink-0 md:justify-end">
                    {!isPaid ? (
                      <button onClick={() => toggleManualPaid(it.invoice_id, true)}
                              className="flex-1 md:flex-none min-w-[120px] justify-center rounded-lg bg-emerald-400 hover:bg-emerald-300 text-emerald-950 px-3 py-2 text-xs font-bold inline-flex items-center gap-1.5"
                              data-testid={`fl-manual-mark-paid-${it.invoice_id}`}>
                        <Wallet size={12} /> Marcar como Paga
                      </button>
                    ) : (
                      <button onClick={() => toggleManualPaid(it.invoice_id, false)}
                              className="flex-1 md:flex-none min-w-[100px] justify-center rounded-lg border border-amber-300/40 bg-amber-300/10 px-3 py-2 text-xs text-amber-200 hover:bg-amber-300/15 inline-flex items-center gap-1.5"
                              data-testid={`fl-manual-mark-unpaid-${it.invoice_id}`}
                              title="Reverter para «por receber» (sai das finanças)">
                        <Undo2 size={12} /> Anular pagamento
                      </button>
                    )}
                    {it.pdf_url && (
                      <button onClick={() => viewManualPdf(it.invoice_id, it.invoice_number)}
                         className="flex-1 md:flex-none min-w-[110px] justify-center rounded-lg border border-emerald-300/20 bg-emerald-300/5 px-3 py-2 text-xs text-emerald-100 hover:bg-emerald-300/10 inline-flex items-center gap-1.5"
                         data-testid={`fl-manual-view-${it.invoice_id}`}>
                        <Eye size={12} /> Ver
                      </button>
                    )}
                    <button onClick={() => downloadManualPdf(it.invoice_id, it.invoice_number)}
                            className="flex-1 md:flex-none min-w-[110px] justify-center rounded-lg border border-emerald-300/20 bg-emerald-300/5 px-3 py-2 text-xs text-emerald-100 hover:bg-emerald-300/10 inline-flex items-center gap-1.5"
                            data-testid={`fl-manual-download-${it.invoice_id}`}>
                      <Download size={12} /> PDF
                    </button>
                    {it.customer_email && (
                      <button onClick={() => resendManualEmail(it.invoice_id)}
                              className="flex-1 md:flex-none min-w-[110px] justify-center rounded-lg border border-amber-300/30 bg-amber-300/10 px-3 py-2 text-xs text-amber-100 hover:bg-amber-300/15 inline-flex items-center gap-1.5"
                              data-testid={`fl-manual-email-${it.invoice_id}`}>
                        <Mail size={12} /> Reenviar
                      </button>
                    )}
                    {(it.customer_nif || it.customer_email) && (
                      <button onClick={() => saveManualCustomer(it.invoice_id, it.customer_name)}
                              className="flex-1 md:flex-none min-w-[110px] justify-center rounded-lg border border-emerald-400/40 bg-emerald-400/10 px-3 py-2 text-xs text-emerald-100 hover:bg-emerald-400/20 inline-flex items-center gap-1.5"
                              data-testid={`fl-manual-save-customer-${it.invoice_id}`}
                              title="Guardar cliente no livro para reutilização futura">
                        <UserPlus size={12} /> Guardar cliente
                      </button>
                    )}
                    <button onClick={() => issueCreditNote(it.invoice_id, it.invoice_number, it.amount)}
                            className="flex-1 md:flex-none min-w-[130px] justify-center rounded-lg border border-rose-400/40 bg-rose-400/10 px-3 py-2 text-xs text-rose-100 hover:bg-rose-400/20 inline-flex items-center gap-1.5"
                            data-testid={`fl-manual-credit-note-${it.invoice_id}`}
                            title="Emitir Nota de Crédito para esta fatura">
                      <Undo2 size={12} /> Nota de Crédito
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Livro de Clientes — modal com histórico + total faturado por cliente.
          Aberto pelo botão «Livro de Clientes» no topo da página. */}
      {showCustomerBook && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
          onClick={() => setShowCustomerBook(false)}
          data-testid="customer-book-modal"
        >
          <div
            className="relative w-full max-w-5xl my-4 sm:my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowCustomerBook(false)}
              className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4 z-10 rounded-full bg-emerald-950 border border-amber-300/40 text-amber-200 hover:text-white hover:bg-emerald-900 p-2 shadow-lg"
              data-testid="customer-book-close"
              title="Fechar"
            >
              <X size={18} />
            </button>
            <CustomerBookSection />
          </div>
        </div>
      )}

      {/* Pré-visualização (detalhes) — funciona quer a fatura já esteja emitida ou não */}
      {previewBid && (
        <InvoicePreviewModal
          bookingId={previewBid}
          onClose={() => setPreviewBid(null)}
          onConfirm={async ({ skipInvoice = false, paymentMethod } = {}) => {
            const bid = previewBid;
            setPreviewBid(null);
            if (skipInvoice) {
              // Mark paid in cash, skip Facturalusa.
              try {
                const params = { payment_status: "paid", skip_invoice: true };
                if (paymentMethod) params.payment_method = paymentMethod;
                await api.patch(`/admin/bookings/${bid}/payment`, null, { params });
                toast.success("Pagamento em dinheiro registado. Sem fatura emitida.");
                load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Falhou.");
              }
            } else {
              await issueNow(bid);
            }
          }}
        />
      )}

      {/* Nova Fatura ou Editar Rascunho */}
      {(showManualModal || editingDraft) && (
        <ManualInvoiceModal
          initialDraft={editingDraft}
          onClose={() => { setShowManualModal(false); setEditingDraft(null); }}
          onIssued={() => { setShowManualModal(false); setEditingDraft(null); load(); }}
          onDraftSaved={() => { load(); }}
        />
      )}

      {/* In-app PDF viewer (mobile-friendly) */}
      <PdfViewerModal
        open={!!pdfView}
        onClose={() => setPdfView(null)}
        title={pdfView?.title}
        inlineUrl={pdfView?.inlineUrl}
        downloadUrl={pdfView?.downloadUrl}
        filename={pdfView?.filename}
      />
    </div>
  );
}
